import { Component, EventEmitter, Inject, Input, OnInit, Output, inject } from "@angular/core"
import { FormGroup } from "@angular/forms"
import { Observable, Subject, catchError, debounceTime, filter, finalize, iif, of, skip, switchMap, tap } from "rxjs"
import { BaseFilterOptions } from "./dashboard-filter-button/base-filter-options.model"
import { BoardFilterService } from "../../common-features/services/board-filter.service"
import { Store } from "@ngrx/store"
import { GlobalSpinnerActions } from "../global-spinner/actions-types"
import { BehaviorEventEmitter } from "../../utils/lang/behavior-event-emitter"

@Component({
    selector: '',
    template: '',
    standalone: false
})
export abstract class DashboardFilterContentBase<TOptions extends BaseFilterOptions, TFormGroup extends FormGroup> implements OnInit {

    @Input() resetFilter$: Observable<void> | undefined

    @Output() filter = new Subject<TOptions>()

    filterFormGroup: TFormGroup | undefined
    filterOptions: TOptions | undefined

    protected appStore = inject(Store)

    constructor(protected readonly filterService: BoardFilterService<TOptions>) {

        this.filter.pipe(
            skip(1),
            debounceTime(1000),
            switchMap((option) => this.filterService.saveFilterState$({ option: option!, form: this.filterFormGroup!.value })) // wait for the options
        ).subscribe()

        of(void 0).pipe(
            tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
            switchMap(() => this.filterService.getFilterState$()),
            tap(state => {
                this.filterOptions = !!state ? state.option : this.createFilterOption()
                this.filterFormGroup = this.createFormGroup(!!state ? state.form : null)
            }),
            catchError(e => {
                this.filterOptions = this.createFilterOption()
                this.filterFormGroup = this.createFormGroup(null)

                this.filterService.saveFilterState$({
                    form: this.filterFormGroup.value,
                    option: this.filterOptions
                }).subscribe()

                return of(void 0)
            }),
            tap(() => {
                this.subscribeToForm(this.filterFormGroup!)
                this.filter.next(this.filterOptions!)
            }),
            finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner()))
        ).subscribe()

    }

    ngOnInit() {
        this.resetFilter$?.subscribe(() => {
            this.filterFormGroup!.reset(this.createFormGroup(null), { emitEvent: false })
            this.filterOptions = this.createFilterOption()
            this.filter.next(this.filterOptions)
        })
    }

    protected abstract createFormGroup(value: any | null): TFormGroup
    protected abstract createFilterOption(): TOptions
    protected abstract subscribeToForm(form: TFormGroup): void
}